import {
  useState,
  useLayoutEffect,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { connect } from 'react-redux';
import { match as Match } from 'react-router';
import { usePagination, PaginatedList, PATHS } from '@belong/common';
import { Text, Tabs } from '@belong/ui';
import { BillInfoDetailModel, PaymentAccountResourceModel } from 'api/models';
import {
  MonthlyStatementDetailInfoModel,
  UnderReviewInfoModel,
  BillingInfoModel,
  GroupedBalancesInfoModel,
} from 'api/models.v2';
import PaymentMethodContainer from 'containers/PaymentMethodContainer/PaymentMethodContainer';
import {
  format,
  endOfMonth,
  startOfMonth,
  addMonths,
  subMonths,
} from 'date-fns';
import { History } from 'history';
import { useFeatureFlag } from 'hooks/use-account-redesign';
import { PaymentPreference, BillType } from 'models/enums/index';
import HomeownerAccountProperty from 'models/homeownerAccounts/HomeownerAccountProperty';
import { HOMEOWNER_AUDIENCE } from 'pages/Accounts/Components/FailedPayments/failed-payments-audience';
import { FailedPaymentsSection } from 'pages/Accounts/Components/FailedPayments/failed-payments-section';
import { mapPaymentAccountToPaymentMethodSummary } from 'pages/Accounts/Components/FailedPayments/failed-payments-utils';
import { ResolveFailedPaymentModal } from 'pages/Accounts/Components/FailedPayments/resolve-failed-payment-modal';
import { useFailedPayments } from 'pages/Accounts/Components/FailedPayments/use-failed-payments';
import { HomeMenu } from 'pages/Accounts/Components/home-menu/home-menu';
import {
  PastMonthDetail,
  PastMonthsSection,
} from 'pages/Accounts/Components/past-months-section';
import { UnderReviewSection } from 'pages/Accounts/Components/under-review-section';
import { UpcomingSection } from 'pages/Accounts/Components/upcoming-section';
import AccountPage from 'pages/Accounts/Pages/AccountPage/AccountPage';
import AccountPageSection from 'pages/Accounts/Pages/AccountPage/AccountPageSection/AccountPageSection';
import {
  balancesDataMapper,
  underReviewDataMapper,
} from 'pages/Accounts/utils/balances-data-mapper';
import queryString from 'query-string';
import {
  fetchUserPastBills,
  fetchUserGroupBalances,
  fetchUserBalancesUnderReview,
  fetchUserGroupBalancesByMonth,
  fetchUserBalancesPastMonths,
  fetchUserBillsById,
} from 'store/redux/accounts/actions';
import {
  selectBalancesUnderReview,
  selectBalancesByMonth,
  selectBalancesPastMonths,
} from 'store/redux/accounts/selectors';
import { selectAccountProperties } from 'store/redux/homeowner-accounts/selectors';
import { selectFeatureFlags } from 'store/redux/settings';
import { AppState } from 'store/redux/types';
import { fetchPaymentMethods, getUserDevices } from 'store/redux/user/actions';
import { selectPaymentMethods } from 'store/redux/user/selectors';
import { getDefaultPaymentMethod } from '../../YourHome/Rent/PayNow/pay-now-utils';
import { PastEarningsSection } from '../../../Components/past-earnings-section';
import { PaymentBillDetailModal } from '../../../Components/payment-bill-detail-modal/payment-bill-detail-modal';
import { PayoutBillDetailModal } from '../../../Components/payout-bill-detail-modal/payout-bill-detail-modal';
import BalanceDetailsModal from './BalanceDetailsModal/BalanceDetailsModal';
import FinanceCloseModal from './FinanceCloseModal/FinanceCloseModal';
import FinanceDetailsModal from './FinanceDetailsModal/FinanceDetailsModal';
import FinanceSuccessModal from './FinanceSuccessModal/FinanceSuccessModal';
import MobileBanner from './MobileBanner/MobileBanner';
import { EARNINGS_STRINGS } from './earnings.strings';
import { PastMonthModal } from './past-month-modal/past-month-modal';

// `billInfo` is optional on the model, so the bill type has to be reached
// through `NonNullable` rather than indexed directly.
type BillInfoType = NonNullable<BillInfoDetailModel['billInfo']>['type'];

type EarningsProps = {
  match: Match<{ propertyId: string }>;
  history: History;
  location: Location;
  // Nullable to match `mapStateProps`: `selectAccountProperties` returns `null`
  // until the properties have been fetched.
  properties: HomeownerAccountProperty[] | null;
  balances: MonthlyStatementDetailInfoModel[] | null;
  balancesUnderReview: UnderReviewInfoModel[] | null;
  pastMonthsBalances: MonthlyStatementDetailInfoModel[] | null;
  paymentMethods: PaymentAccountResourceModel[];
  fetchPaymentMethods: () => Promise<unknown>;
  // Returns the fetched bill, not `void`: `clientMiddleware` resolves a
  // `promise` action to the client's result, and `fetchUserBillsOnMount` below
  // awaits this and reads `billInfo` off it.
  fetchUserBillsById: (
    id?: string,
    type?: BillInfoType,
  ) => Promise<BillInfoDetailModel | undefined>;
  fetchUserPastBills: (
    queryParams?: string,
  ) => Promise<PaginatedList<BillInfoDetailModel>>;
  fetchUserGroupBalancesByMonth: (queryParams?: string) => void;
  fetchUserBalancesPastMonths: (queryParams?: string) => void;
  fetchUserBalancesUnderReview: (queryParams?: string) => void;
  getUserDevices: () => Promise<
    Array<{
      isActive: boolean;
    }>
  >;
  isAutoSplitFeatureFlagEnabled?: boolean;
};

const RESOLVE_FAILED_PAYMENT_QUERY_PARAM = 'resolveFailedPayment';

const TRANSACTIONS_TABS = [
  { key: 'homeowners-transactions-montly', text: 'Monthly' },
  { key: 'homeowners-transactions-receipts', text: 'Receipts' },
];

function Earnings({
  history,
  match,
  properties,
  balances,
  pastMonthsBalances,
  balancesUnderReview,
  location,
  paymentMethods = [],
  fetchPaymentMethods: fetchPaymentMethodsAction,
  fetchUserBillsById: fetchUserBillsByIdAction,
  fetchUserPastBills: fetchUserPastBillsAction,
  fetchUserGroupBalancesByMonth: fetchUserGroupBalancesByMonthAction,
  fetchUserBalancesPastMonths: fetchUserBalancesPastMonthsAction,
  fetchUserBalancesUnderReview: fetchUserBalancesUnderReviewAction,
  getUserDevices: getUserDevicesAction,
  isAutoSplitFeatureFlagEnabled,
}: EarningsProps) {
  const { search, pathname } = location;
  const queryParams = queryString.parse(search);
  const pastMonthsRef = useRef<HTMLDivElement>(null);

  console.log(
    'process.env.NEXT_PUBLIC_CDN_DOMAIN',
    process.env.NEXT_PUBLIC_CDN_DOMAIN ?? 'No CDN domain found from Vercel',
  );

  const [loading, setLoading] = useState(true);
  const [selectedHome, setSelectedHome] = useState<string>(
    queryParams.homeId
      ? typeof queryParams.homeId === 'string'
        ? queryParams.homeId
        : queryParams.homeId?.[0]
      : 'all',
  );
  // Typed rather than reseeded: `useState(null)` gave the state the type `null`,
  // so it could never hold the bill type the handlers below put in it. The
  // initial value is unchanged.
  const [type, setType] = useState<BillInfoType | null>(null);
  const [billId, setBillId] = useState(queryParams.id || null);
  const [financeDetailModal, setFinanceDetailModal] = useState(false);
  const [financeSuccessModal, setFinanceSuccessModal] = useState(false);
  const [financeCloseModal, setFinanceCloseModal] = useState(false);
  const [financePlanSelected, setFinancePlanSelected] = useState<string>();
  const [balanceDetailsModal, setBalanceDetailsModal] = useState(false);
  const [selectedPastMonth, setSelectedPastMonth] =
    useState<PastMonthDetail | null>(null);
  const [selectedBalance, setSelectedBalance] = useState<
    | {
        balance: GroupedBalancesInfoModel;
        address: string;
        billingInfo?: BillingInfoModel;
        autosplittable?: boolean;
      }
    | undefined
  >();
  const [balanceStandalone, setBalanceStandalone] = useState(false);
  const [hasApp, setHasApp] = useState(true);
  const [_selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [showResolveFailedPaymentsModal, setShowResolveFailedPaymentsModal] =
    useState(false);

  const {
    isEnabled: isFailedPaymentsEnabled,
    isReady: isFailedPaymentsFlagReady,
  } = useFeatureFlag('failed-payments-homeowners');

  const { failedPayments, fetchFailedPayments } = useFailedPayments();
  const shouldShowFailedPayments =
    isFailedPaymentsEnabled && failedPayments.length > 0;
  const pendingFailedPaymentResolution = useRef(
    new URLSearchParams(search).get(RESOLVE_FAILED_PAYMENT_QUERY_PARAM) ===
      'true',
  );
  const defaultPaymentMethod = useMemo(
    () =>
      mapPaymentAccountToPaymentMethodSummary(
        getDefaultPaymentMethod(paymentMethods, true, false),
      ),
    [paymentMethods],
  );

  const restParams = useMemo(
    () => (selectedHome === 'all' ? {} : { homeUniqueIds: [selectedHome] }),
    [selectedHome],
  );

  const {
    items: bills,
    loadMore,
    hasMorePages,
  } = usePagination({ fetch: fetchUserPastBillsAction, restParams });
  // The first read is asserted, not optional-chained: it already threw on a null
  // `properties` before this was typed, and turning it into a skip would flip
  // `hasManyHomes` on that path. The `?.` on the second read is the original.
  const hasManyHomes =
    properties!.length > 1 ||
    !!properties?.some((property) => property.units.length > 1);

  const fetchData = useCallback(
    async function () {
      setLoading(true);

      const selectedHomeParam = JSON.parse(
        JSON.stringify({
          homeUniqueIds: selectedHome === 'all' ? undefined : [selectedHome],
        }),
      );

      const from = startOfMonth(new Date());
      const until = endOfMonth(addMonths(from, 1));

      const balancesQueryParams = queryString.stringify({
        ...selectedHomeParam,
        from: `${format(from, 'yyyy-MM-dd')}T00:00:00.000Z`,
        until: `${format(until, 'yyyy-MM-dd')}T23:59:59.999Z`,
        groupBalanceType: 'ExpensesAndRevenues',
      });

      const pastBalancesQueryParams = queryString.stringify({
        ...selectedHomeParam,
        from: `${format(subMonths(from, 12), 'yyyy-MM-dd')}T00:00:00.000Z`,
        until: `${format(subMonths(until, 2), 'yyyy-MM-dd')}T23:59:59.999Z`,
        groupBalanceType: 'ExpensesAndRevenues',
      });

      try {
        await fetchUserGroupBalancesByMonthAction(balancesQueryParams);
        await fetchUserBalancesPastMonthsAction(pastBalancesQueryParams);
        await fetchUserBalancesUnderReviewAction(
          queryString.stringify({ ...selectedHomeParam }),
        );
        await Promise.all([fetchPaymentMethodsAction(), fetchFailedPayments()]);
        const devices = await getUserDevicesAction();

        if (devices.length > 0) setHasApp(true);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [
      fetchUserGroupBalancesByMonthAction,
      fetchUserBalancesUnderReviewAction,
      fetchUserBalancesPastMonthsAction,
      fetchPaymentMethodsAction,
      fetchFailedPayments,
      getUserDevicesAction,
      selectedHome,
    ],
  );

  function handleModalHide() {
    setBillId(null);
    setType(null);

    history.push(PATHS.HOMEOWNER_ACCOUNT_EARNINGS);
  }

  const clearFailedPaymentResolutionQuery = useCallback(() => {
    const searchParams = new URLSearchParams(search);

    if (!searchParams.has(RESOLVE_FAILED_PAYMENT_QUERY_PARAM)) return;

    searchParams.delete(RESOLVE_FAILED_PAYMENT_QUERY_PARAM);
    const nextSearch = searchParams.toString();

    history.replace(`${pathname}${nextSearch ? `?${nextSearch}` : ''}`);
  }, [history, pathname, search]);

  async function handleResolveFailedPaymentsClick() {
    const latestFailedPayments = await fetchFailedPayments();

    if (latestFailedPayments.length) {
      setShowResolveFailedPaymentsModal(true);
    }
  }

  function handleHideResolveFailedPaymentsModal() {
    setShowResolveFailedPaymentsModal(false);
    clearFailedPaymentResolutionQuery();
  }

  async function handleFailedPaymentsResolved() {
    setShowResolveFailedPaymentsModal(false);
    clearFailedPaymentResolutionQuery();
    await fetchData();
  }

  // The signature matches what the sections below declare for this prop. `id`
  // arrives as a balance's `sourceKey` — a string — while the declaration also
  // admits `number`, so it is narrowed on the way into state, which both finance
  // modals declare as a string.
  function handleFinancingModal(
    id: string | number,
    balance: GroupedBalancesInfoModel,
    address: string,
  ) {
    setSelectedBalance({ balance, address });
    setFinancePlanSelected(id as string);
    setFinanceDetailModal(true);
  }

  function handleShowPastMonthModal(detail: PastMonthDetail) {
    setSelectedPastMonth(detail);
  }

  function handleFinancingModalHide() {
    setFinanceDetailModal(false);
  }

  function handleFinancingSuccessModalHide() {
    setFinanceSuccessModal(false);
  }

  function handleBalanceDetailsModalHide() {
    setBalanceDetailsModal(false);
  }

  function handleFinancingCloseModalHide() {
    fetchData();
    setFinanceCloseModal(false);
  }

  function handleShowFinanceSuccessModal() {
    fetchData();
    setFinanceSuccessModal(true);
  }

  function handleShowBalanceDetailsModal(
    balance: GroupedBalancesInfoModel,
    address: string,
    billingInfo?: BillingInfoModel,
    standalone = false,
    autosplittable = false,
  ) {
    setBalanceDetailsModal(true);
    setSelectedBalance({ balance, address, billingInfo, autosplittable });
    setBalanceStandalone(standalone);
  }

  function handleHomeMenuChange(homeId: string) {
    setSelectedHome(homeId);

    history.push({
      pathname,
      search: `?homeId=${homeId}`,
    });
  }

  function handleUngroupModal(id: string) {
    setFinanceCloseModal(true);
    setFinancePlanSelected(id);
  }

  function onTabIndexChange(index: number) {
    setSelectedTabIndex(index);
  }

  function handleShowEarningsModal(bill: BillInfoDetailModel) {
    return async () => {
      setLoading(true);

      // `billInfo` is optional on the model, but every bill the earnings list
      // renders carries one and each read here was already unconditional. The
      // assertions are erased at runtime, so a bill without one still throws
      // where it did.
      try {
        await fetchUserBillsByIdAction(bill.billInfo!.id, bill.billInfo!.type);
      } catch (err) {
        console.error(err);
      }

      setBillId(bill.billInfo!.id);
      setType(bill.billInfo!.type);
      setLoading(false);

      history.push(`${PATHS.HOMEOWNER_ACCOUNT_EARNINGS}/${bill.billInfo!.id}`);
    };
  }

  useLayoutEffect(() => {
    async function fetchUserBillsOnMount() {
      if (match.params.propertyId) {
        let bill;
        try {
          bill = await fetchUserBillsByIdAction(match.params.propertyId);
        } catch (err) {
          console.error(err);
        }

        setBillId(match.params.propertyId);
        // `?? null` because the state is seeded with `null` and both are read
        // only by `type === BillType.…` comparisons, which are false either way.
        setType(bill?.billInfo?.type ?? null);
      }
    }

    fetchUserBillsOnMount();
  }, [fetchUserBillsByIdAction, match.params.propertyId]);

  useLayoutEffect(() => {
    if (!loading && queryParams.scroll === 'pastMonths') {
      pastMonthsRef.current!.scrollIntoView({ block: 'center' });
    }
  }, [loading, queryParams.scroll]);

  useEffect(() => {
    fetchData();
  }, [selectedHome, fetchUserGroupBalancesByMonthAction, fetchData]);

  useEffect(() => {
    if (!pendingFailedPaymentResolution.current) return;
    if (loading || !isFailedPaymentsFlagReady) return;

    pendingFailedPaymentResolution.current = false;

    if (!isFailedPaymentsEnabled) return;

    if (failedPayments.length) {
      setShowResolveFailedPaymentsModal(true);
    } else {
      clearFailedPaymentResolutionQuery();
    }
  }, [
    loading,
    isFailedPaymentsFlagReady,
    isFailedPaymentsEnabled,
    failedPayments,
    clearFailedPaymentResolutionQuery,
  ]);

  return (
    <AccountPage
      loading={loading}
      belowHeaderContent={
        shouldShowFailedPayments ? (
          <FailedPaymentsSection
            failedPayments={failedPayments}
            onResolveClick={handleResolveFailedPaymentsClick}
          />
        ) : null
      }
      headerProps={{
        children: (
          <div className="flex items-center justify-between">
            <Text variant="h2" fontWeight="semibold">
              {EARNINGS_STRINGS['page.title']}
            </Text>
            {hasManyHomes && (
              <HomeMenu
                // Reached only when `hasManyHomes` is true, which already read
                // `properties.length` and so cannot be null here.
                properties={properties!}
                selectedHomeId={selectedHome}
                allHomesLabel={EARNINGS_STRINGS.all_homes}
                onChange={handleHomeMenuChange}
              />
            )}
          </div>
        ),
      }}
    >
      <div>
        <Tabs
          hasBlurOnScroll={false}
          index={_selectedTabIndex}
          onChange={onTabIndexChange}
        >
          <Tabs.TabList className="mx-auto w-fit">
            {TRANSACTIONS_TABS.map((tab, index) => {
              return (
                <Tabs.TabItem key={tab.key} index={index}>
                  <p>{tab.text}</p>
                </Tabs.TabItem>
              );
            })}
          </Tabs.TabList>
          <Tabs.PanelItem index={0} className="pt-xl">
            <UpcomingSection
              balances={balances}
              selectedHome={selectedHome}
              handleFinancingModal={handleFinancingModal}
              handleShowFinanceSuccessModal={handleShowFinanceSuccessModal}
              handleShowBalanceDetailsModal={handleShowBalanceDetailsModal}
              hasManyHomes={hasManyHomes}
            />
            <UnderReviewSection
              homesBalances={balancesUnderReview}
              selectedHome={selectedHome}
              handleFinancingModal={handleFinancingModal}
              handleShowFinanceSuccessModal={handleShowFinanceSuccessModal}
              handleShowBalanceDetailsModal={handleShowBalanceDetailsModal}
              hasManyHomes={hasManyHomes}
            />

            {!hasApp && <MobileBanner hasApp={hasApp} />}

            <PastMonthsSection
              balances={pastMonthsBalances}
              handleShowPastMonthModal={handleShowPastMonthModal}
              isResident={false}
              selectedHome={selectedHome}
              hasManyHomes={hasManyHomes}
              ref={pastMonthsRef}
            />
            <AccountPageSection
              title={EARNINGS_STRINGS['section.bank.title']}
              noPaddingTop
              content={
                <PaymentMethodContainer
                  paymentType={PaymentPreference.Payout}
                  isRenter={false}
                  shouldShowAutoSplitPaymentMethod={
                    isAutoSplitFeatureFlagEnabled
                  }
                />
              }
            />
          </Tabs.PanelItem>
          <Tabs.PanelItem index={1} className="pt-2xl">
            <PastEarningsSection
              earnings={bills}
              selectedHome={selectedHome}
              handleShowEarningsModal={handleShowEarningsModal}
              loadMore={loadMore}
              hasMorePages={hasMorePages}
              hasManyHomes={hasManyHomes}
            />
          </Tabs.PanelItem>
        </Tabs>
      </div>
      <PaymentBillDetailModal
        show={type === BillType.Payment}
        onHide={handleModalHide}
        billId={typeof billId === 'string' ? billId : billId?.[0]}
        isHomeowner
        handleFinancingModal={handleFinancingModal}
        handleShowBalanceDetailsModal={handleShowBalanceDetailsModal}
        home={selectedBalance?.address}
      />
      <PayoutBillDetailModal
        show={type === BillType.Payout}
        onHide={handleModalHide}
        handleFinancingModal={handleFinancingModal}
        handleShowBalanceDetailsModal={handleShowBalanceDetailsModal}
        home={selectedBalance?.address}
      />
      <FinanceDetailsModal
        show={financeDetailModal}
        onHide={handleFinancingModalHide}
        financeUniqueId={financePlanSelected}
        handleUngroupModal={handleUngroupModal}
        handleShowBalanceDetailsModal={handleShowBalanceDetailsModal}
        home={selectedBalance?.address}
      />
      <FinanceCloseModal
        show={financeCloseModal}
        financeUniqueId={financePlanSelected}
        onHide={handleFinancingCloseModalHide}
      />
      <FinanceSuccessModal
        show={financeSuccessModal}
        onHide={handleFinancingSuccessModalHide}
      />
      <BalanceDetailsModal
        show={balanceDetailsModal}
        balance={selectedBalance}
        onHide={handleBalanceDetailsModalHide}
        standalone={balanceStandalone}
        handleShowFinanceSuccessModal={handleShowFinanceSuccessModal}
      />
      {!!selectedPastMonth && (
        <PastMonthModal
          show
          homeDetail={selectedPastMonth}
          onHide={() => setSelectedPastMonth(null)}
          handleShowBalanceDetailsModal={handleShowBalanceDetailsModal}
          handleFinancingModal={handleFinancingModal}
          handleShowFinanceSuccessModal={handleShowFinanceSuccessModal}
        />
      )}
      <ResolveFailedPaymentModal
        show={shouldShowFailedPayments && showResolveFailedPaymentsModal}
        audience={HOMEOWNER_AUDIENCE}
        failedPayments={failedPayments}
        defaultPaymentMethod={defaultPaymentMethod}
        paymentMethods={paymentMethods}
        onHide={handleHideResolveFailedPaymentsModal}
        onResolved={handleFailedPaymentsResolved}
      />
    </AccountPage>
  );
}

function mapStateProps(state: AppState) {
  return {
    balances: balancesDataMapper(selectBalancesByMonth(state)),
    pastMonthsBalances: balancesDataMapper(selectBalancesPastMonths(state)),
    properties: selectAccountProperties(state),
    isAutoSplitFeatureFlagEnabled:
      !!selectFeatureFlags(state).isAutoSplitEnabled,
    balancesUnderReview: underReviewDataMapper(
      selectBalancesUnderReview(state),
    ),
    paymentMethods: selectPaymentMethods(
      state,
    ) as PaymentAccountResourceModel[],
  };
}
const mapDispatchToProps = {
  // `queryParams` is optional to match the prop `Earnings` declares for it —
  // `usePagination` calls the action with no arguments on its first page.
  fetchUserPastBills: fetchUserPastBills as unknown as (
    queryParams?: string,
  ) => Promise<PaginatedList<BillInfoDetailModel>>,
  fetchUserBillsById,
  fetchUserGroupBalances,
  fetchUserGroupBalancesByMonth,
  fetchUserBalancesPastMonths,
  fetchUserBalancesUnderReview,
  fetchPaymentMethods: fetchPaymentMethods as unknown as () => Promise<unknown>,
  // Cast for the same reason as `fetchUserPastBills` above: `connect` types the
  // prop as the action creator's own return — the `{ promise, auth }` action —
  // while `clientMiddleware` resolves it to the client's result, which is what
  // `fetchData` awaits.
  getUserDevices: getUserDevices as unknown as () => Promise<
    Array<{ isActive: boolean }>
  >,
};

export default connect(mapStateProps, mapDispatchToProps)(Earnings);
